// src/Dashboard/admin/sections/LearnersSection.jsx
import { useMemo, useState } from "react";
import { RefreshCw, Search, Users, Eye, Info, Filter } from "lucide-react";

/**
 * LearnersSection (UI-only)
 * - 4 columns only: Name(+email), Church, Status, More
 * - DOES NOT show user id anywhere
 * - "More" calls onOpenLearner(row) (parent opens LearnerDrawer)
 * - Robust learners prop normalization
 */

export default function LearnersSection({
  // data (can be array OR object wrappers)
  learners = [],
  total = null,
  stats = null,

  // loading / error
  loading = false,
  error = "",

  // filters (controlled)
  q = "",
  onQChange,
  status = "all",
  onStatusChange,

  // optional extra filters (hierarchy selectors etc.)
  extraFilters,

  // actions
  onRefresh,

  // row action
  onOpenLearner, // (learnerRow)=>void

  // options
  statusOptions = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ],

  // helpers (optional)
  churchNameById, // Map(id -> name)
  learnerChurchNameFallback, // (row, churchNameById)=>string
}) {
  // normalize learners into list
  const list = useMemo(() => {
    if (Array.isArray(learners)) return learners;
    if (Array.isArray(learners?.data)) return learners.data;
    if (Array.isArray(learners?.rows)) return learners.rows;
    if (Array.isArray(learners?.items)) return learners.items;
    if (Array.isArray(learners?.data?.data)) return learners.data.data;
    return [];
  }, [learners]);

  const effectiveTotal = useMemo(() => {
    if (typeof total === "number") return total;
    const t =
      learners?.total ??
      learners?.count ??
      learners?.pagination?.total ??
      learners?.meta?.total ??
      null;
    return typeof t === "number" ? t : null;
  }, [total, learners]);

  const derivedStats = useMemo(() => {
    if (stats) return stats;
    const norm = list.map(normalizeLearner);
    const active = norm.filter((x) => x.status === "active").length;
    const inactive = Math.max(0, norm.length - active);
    return {
      total: effectiveTotal ?? norm.length,
      active,
      inactive,
    };
  }, [stats, list, effectiveTotal]);

  const [debug, setDebug] = useState(false);

  return (
    <div className="space-y-6 min-w-0">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm min-w-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between min-w-0">
          <div className="min-w-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-xl bg-orange-50 dark:bg-orange-500/10 shrink-0">
                <Users size={20} className="text-orange-600 dark:text-orange-300" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold truncate">Learners</h2>
                <p className="text-xs text-slate-500 truncate">
                  Simple list: Name, Church, Status, More.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {onRefresh ? (
              <button
                type="button"
                onClick={onRefresh}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 hover:bg-slate-50 dark:hover:bg-slate-900 text-sm"
              >
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
            ) : null}

            <button
              type="button"
              onClick={() => setDebug((v) => !v)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900 text-sm"
              title="Toggle debug"
            >
              <Info size={16} />
              Debug
            </button>
          </div>
        </div>

        {/* Mini stats */}
        <div className="mt-5 grid grid-cols-3 gap-3">
          <MiniKpi label="Total" value={fmtInt(derivedStats?.total ?? 0)} />
          <MiniKpi label="Active" value={fmtInt(derivedStats?.active ?? 0)} />
          <MiniKpi label="Inactive" value={fmtInt(derivedStats?.inactive ?? 0)} />
        </div>

        {debug ? (
          <div className="mt-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-4 text-xs text-slate-600 dark:text-slate-300">
            <div className="font-semibold mb-2">Debug snapshot</div>
            <div className="grid md:grid-cols-2 gap-2">
              <div>
                <div>
                  <span className="text-slate-400">learners prop type:</span>{" "}
                  {Array.isArray(learners) ? "array" : typeof learners}
                </div>
                <div>
                  <span className="text-slate-400">normalized list length:</span> {list.length}
                </div>
                <div>
                  <span className="text-slate-400">effectiveTotal:</span>{" "}
                  {effectiveTotal == null ? "null" : String(effectiveTotal)}
                </div>
                <div>
                  <span className="text-slate-400">loading:</span> {String(loading)}
                </div>
                <div>
                  <span className="text-slate-400">error:</span> {error ? String(error) : "—"}
                </div>
              </div>
              <div>
                <div className="text-slate-400 mb-1">First row keys (if any):</div>
                <div className="font-mono break-words">
                  {list?.[0] ? Object.keys(list[0]).slice(0, 24).join(", ") : "—"}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Filters */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">
          <SearchField value={q} onChange={onQChange} placeholder="Search name or email…" />
          <SelectField label="Status" value={status} onChange={onStatusChange} options={statusOptions} icon={Filter} />
          {extraFilters ? <div className="md:col-span-2 min-w-0">{extraFilters}</div> : null}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden min-w-0">
        <div className="px-4 py-3 bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3 min-w-0">
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
            Results{" "}
            <span className="text-slate-400 font-normal ml-2">
              ({fmtInt(effectiveTotal ?? list.length)})
            </span>
          </div>

          {loading ? (
            <div className="text-xs text-slate-500 flex items-center gap-2 shrink-0">
              <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
              Loading…
            </div>
          ) : null}
        </div>

        {error ? (
          <div className="p-4">
            <div className="rounded-2xl border border-red-200 bg-red-50 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200 p-4 text-sm">
              {String(error)}
            </div>
          </div>
        ) : list.length === 0 ? (
          <EmptyState loading={loading} q={q} status={status} onRefresh={onRefresh} />
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="min-w-[760px] w-full text-sm">
              <thead className="text-xs text-slate-500 bg-white dark:bg-slate-900 sticky top-0 z-10">
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <Th>Name</Th>
                  <Th>Church</Th>
                  <Th>Status</Th>
                  <Th className="text-right">More</Th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {list.map((raw, idx) => {
                  const l = normalizeLearner(raw);

                  const rowKey =
                    String(raw?.id ?? raw?.user_id ?? raw?.email ?? `row_${idx}`);

                  const churchName =
                    l.churchName ||
                    (typeof learnerChurchNameFallback === "function"
                      ? learnerChurchNameFallback(raw, churchNameById)
                      : "") ||
                    (churchNameById?.get?.(
                      String(raw?.church_id ?? raw?.churchId ?? "")
                    ) || "");

                  return (
                    <tr key={rowKey} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      {/* Name + Email (NO ID) */}
                      <Td>
                        <div className="flex items-center gap-3 min-w-0">
                          <AvatarInitials name={l.name || l.email} />
                          <div className="min-w-0">
                            <div className="font-semibold truncate">{l.name || "Learner"}</div>
                            <div className="text-xs text-slate-500 truncate">{l.email || "—"}</div>
                          </div>
                        </div>
                      </Td>

                      {/* Church */}
                      <Td>
                        <div className="font-medium text-slate-800 dark:text-slate-100 truncate">
                          {churchName || "—"}
                        </div>
                        {/* subtle context (optional) */}
                        {(l.districtName || l.conferenceName) ? (
                          <div className="text-xs text-slate-500 truncate">
                            {l.districtName || l.stationName || "—"}
                            {l.conferenceName ? ` • ${l.conferenceName}` : ""}
                          </div>
                        ) : null}
                      </Td>

                      {/* Status */}
                      <Td>
                        <Badge
                          tone={l.status === "active" ? "success" : "neutral"}
                          label={l.status === "active" ? "Active" : "Inactive"}
                        />
                      </Td>

                      {/* More */}
                      <Td className="text-right">
                        <button
                          type="button"
                          onClick={() => onOpenLearner?.(raw)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs font-semibold"
                        >
                          <Eye size={14} />
                          More
                        </button>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------- Helpers -------------------------------- */

function normalizeLearner(x) {
  const name =
    x?.fullName ||
    x?.full_name ||
    x?.name ||
    [x?.first_name, x?.last_name].filter(Boolean).join(" ") ||
    "";

  const email = x?.email ?? x?.email_address ?? "";
  const conferenceName = x?.conferenceName ?? x?.conference_name ?? x?.conference ?? "";
  const stationName = x?.stationName ?? x?.station_name ?? x?.station ?? "";
  const districtName = x?.districtName ?? x?.district_name ?? x?.district ?? "";
  const churchName = x?.churchName ?? x?.church_name ?? x?.church ?? "";

  const isActive =
    x?.isActive != null
      ? !!x.isActive
      : x?.is_active != null
      ? x.is_active !== false && x.is_active !== 0 && x.is_active !== "0"
      : true;

  return {
    name: String(name || "").trim(),
    email: String(email || "").trim(),
    conferenceName: String(conferenceName || "").trim(),
    stationName: String(stationName || "").trim(),
    districtName: String(districtName || "").trim(),
    churchName: String(churchName || "").trim(),
    status: isActive ? "active" : "inactive",
  };
}

function fmtInt(v) {
  try {
    return new Intl.NumberFormat().format(Number(v || 0));
  } catch {
    return String(v ?? 0);
  }
}

/* --------------------------------- UI bits -------------------------------- */

function Th({ children, className = "" }) {
  return <th className={`text-left font-semibold px-4 py-3 whitespace-nowrap ${className}`}>{children}</th>;
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}

function SearchField({ value, onChange, placeholder }) {
  return (
    <div className="min-w-0">
      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Search</label>
      <div className="relative min-w-0">
        <input
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          className="w-full px-10 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
        />
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, icon: Icon }) {
  return (
    <div className="min-w-0">
      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          className="w-full pl-3 pr-9 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm appearance-none"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {Icon ? (
          <Icon size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        ) : null}
      </div>
    </div>
  );
}

function Badge({ tone = "neutral", label }) {
  const styles =
    tone === "success"
      ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-200 border-green-200 dark:border-green-800"
      : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700";

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[11px] font-semibold ${styles}`}>
      {label}
    </span>
  );
}

function AvatarInitials({ name }) {
  const initials =
    String(name || "")
      .trim()
      .split(" ")
      .filter(Boolean)
      .map((x) => x[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "U";

  return (
    <div className="w-10 h-10 rounded-full bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 flex items-center justify-center font-semibold shrink-0">
      {initials}
    </div>
  );
}

function MiniKpi({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-4 min-w-0">
      <div className="text-[11px] text-slate-500 truncate">{label}</div>
      <div className="text-base font-semibold truncate">{String(value)}</div>
    </div>
  );
}

function EmptyState({ loading, q, status, onRefresh }) {
  const hasFilters = Boolean(String(q || "").trim()) || (status && status !== "all");
  return (
    <div className="p-8 text-center">
      <div className="mx-auto w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
        <Users size={22} className="text-slate-600 dark:text-slate-200" />
      </div>

      <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
        {loading ? "Loading learners…" : "No learners found"}
      </div>

      <div className="text-xs text-slate-500 mt-1">
        {hasFilters ? "Try changing filters or scope." : "If you expect data, check AdminDashboard wiring (props passed in)."}
      </div>

      {onRefresh ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900 text-sm"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      ) : null}
    </div>
  );
}
